import dayjs, { Dayjs } from "dayjs";
import { TextField } from "@mui/material";
import { FieldValues, UseFormReturn } from "react-hook-form";

import AttachmentBox from "./AttachmentBox";
import { DateTimePickerField } from "./DateTimePickerField";
import AutocompleteField from "../filters/AutoCompleteField";
import { ReactiveAutoCompleteField } from "../filters/ReactiveAutoCompleteField";
import { DocumentFieldType, DocumentFormFieldType } from "../../../types/document-management/document-management-types";

interface DocumentFormFieldProps {
  field: DocumentFormFieldType;
  form: UseFormReturn<FieldValues, any>;
}
const DocumentFormField: React.FC<DocumentFormFieldProps> = ({ field, form }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = form;

  const error = field.id in errors;
  const error_message = errors?.[field.id]?.message ?? "";

  function dateFieldOnChange(value: Dayjs | null, keyboard_value?: string | undefined) {
    if ((!value && !keyboard_value) || value?.isValid()) {
      setValue(field.id, value);
      return;
    }
    const trimmed_date = keyboard_value?.trim();
    if (dayjs(trimmed_date, "DD/MM/YYYY", true).isValid()) {
      setValue(field.id, dayjs(trimmed_date, "DD/MM/YYYY", true));
      return;
    }

    setValue(field.id, value);
  }

  switch (field.type) {
    case DocumentFieldType.Textfield:
      return (
        <TextField
          label={field.label}
          size="small"
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, { required: field.required })}
          error={error}
          helperText={error_message}
        />
      );
    case DocumentFieldType.URL:
      return (
        <TextField
          label={field.label}
          size="small"
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, {
            required: field.required,
            pattern: {
              value: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$|^(https?:\/\/)?(\d{1,3}\.){3}\d{1,3}(:\d+)?(\/[^\s]*)?$/,
              message: "Please enter a valid URL or IP address",
            },
          })}
          error={error}
          helperText={error_message}
        />
      );
    case DocumentFieldType.Number:
      return (
        <TextField
          type="number"
          label={field.label}
          size="small"
          placeholder={field.placeholder ?? field.label}
          disabled={Boolean(field.disabled)}
          {...register(field.id, { required: field.required })}
          error={error}
          inputProps={{ step: "0.001" }}
          helperText={error_message}
        />
      );
    case DocumentFieldType.Dropdown:
      return (
        <AutocompleteField
          id={field.id}
          options={field.options}
          label={field.label}
          onChange={(value: any) => setValue(field.id, value)}
          required={field.required}
          multiple={field.multiple}
          disabled={Boolean(field.disabled)}
          error={error}
          helperText={error_message.toString()}
          {...form}
        />
      );
    case DocumentFieldType.Attachment:
      return <AttachmentBox id={field.id} required={field.required} errors={errors} {...form} />;
    case DocumentFieldType.REACTIVE_DROPDOWN:
      return <ReactiveAutoCompleteField field={field} form={form} />;
    case DocumentFieldType.Date:
      return (
        <DateTimePickerField
          {...field}
          {...form}
          views={["year", "month", "day"]}
          inputFormat="DD/MM/YYYY"
          onChange={dateFieldOnChange}
          error={error}
          helperText={error_message.toString()}
        />
      );
    case DocumentFieldType.DateTime:
      return (
        <DateTimePickerField
          {...field}
          {...form}
          onChange={dateFieldOnChange}
          error={error}
          helperText={error_message.toString()}
        />
      );

    default:
      return <></>;
  }
};

export default DocumentFormField;
